import React, { useState } from "react";
import { Volume2, VolumeX } from "lucide-react";
import { intro, whoWeAre, edge } from "../mock";

export function Intro() {
  const [soundOn, setSoundOn] = useState(false);

  const toggleSound = () => {
    const next = !soundOn;
    document.querySelectorAll("#intro video").forEach((v) => {
      v.muted = !next;
      if (next) {
        const p = v.play();
        if (p && p.catch) p.catch(() => {});
      }
    });
    setSoundOn(next);
  };

  return (
    <section id="intro" className="grad-teal-soft relative overflow-hidden">
      <div className="mx-auto grid max-w-[1500px] items-center gap-10 px-5 py-20 md:grid-cols-2 md:px-10 md:py-28">
        <div className="reveal">
          <span className="mono-label text-xs text-black/60">{intro.kicker}</span>
          <h2 className="font-display mt-4 text-6xl text-black sm:text-7xl md:text-8xl">
            {intro.title.map((t, i) => (
              <span key={i} className="block">
                {t}
                {i === intro.title.length - 1 && <span className="text-black/30">))</span>}
              </span>
            ))}
          </h2>
          <div className="mt-8 max-w-md space-y-4">
            {intro.body.map((b, i) => (
              <p key={i} className="text-lg leading-relaxed text-black/80">{b}</p>
            ))}
          </div>
        </div>

        {/* vertical reel screen */}
        <div className="reveal relative flex justify-center md:justify-end md:pr-8">
          <div
            className="relative w-[min(320px,78vw)] overflow-hidden rounded-[2rem] bg-black shadow-[0_30px_80px_rgba(0,0,0,0.45)] ring-8 ring-black"
            style={{ aspectRatio: "9 / 16" }}
            data-testid="intro-reel-screen"
          >
            <video
              src="/intro-reel.mp4"
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              poster="/intro-poster.jpg"
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-black/20" />
            <button
              onClick={toggleSound}
              aria-label={soundOn ? "Ton aus" : "Ton an"}
              data-testid="intro-reel-sound-toggle"
              className="absolute bottom-4 right-4 flex h-11 w-11 items-center justify-center rounded-full border border-white/50 bg-black/40 text-white backdrop-blur-sm transition-colors hover:border-[var(--crtr-yellow)] hover:text-[var(--crtr-yellow)]"
            >
              {soundOn ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

export function WhoWeAre() {
  return (
    <section id="about" className="relative overflow-hidden bg-transparent py-24 md:py-32">
      <div className="mx-auto max-w-[1500px] px-5 md:px-10">
        <div className="reveal">
          <span className="mono-label text-xs text-[var(--crtr-yellow)]">{whoWeAre.kicker}</span>
          <h2 className="font-display mt-4 max-w-4xl text-6xl text-white sm:text-7xl md:text-8xl">
            {whoWeAre.title[0]} <span className="text-[var(--crtr-yellow)]">{whoWeAre.title[1]}))</span>
          </h2>
          <p className="mt-8 max-w-2xl text-lg leading-relaxed text-white/70">{whoWeAre.body}</p>
        </div>

        <div className="reveal mt-14 grid gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/10 sm:grid-cols-5">
          {whoWeAre.pairs.map((p, i) => (
            <div key={i} className="group bg-black/30 p-6 backdrop-blur-sm transition-colors hover:bg-[var(--crtr-yellow)]">
              <span className="font-heavy block text-lg text-white transition-colors group-hover:text-black">{p[0]}</span>
              <span className="mono-label my-2 block text-xs text-[var(--crtr-yellow)] transition-colors group-hover:text-black/60">meets</span>
              <span className="font-heavy block text-lg text-white transition-colors group-hover:text-black">{p[1]}</span>
            </div>
          ))}
        </div>
        <p className="reveal mt-8 mono-label text-sm text-white/50">{whoWeAre.outro}</p>
      </div>
    </section>
  );
}

export function Edge() {
  return (
    <section className="glow-teal relative overflow-hidden border-t border-white/10 bg-transparent py-24 md:py-32">
      <div className="mx-auto grid max-w-[1500px] items-start gap-10 px-5 md:grid-cols-[1fr_1.1fr] md:px-10">
        <div className="reveal">
          <span className="mono-label text-xs text-[var(--crtr-yellow)]">{edge.kicker}</span>
          <h2 className="font-display mt-4 text-6xl text-white sm:text-7xl md:text-7xl">
            {edge.title[0]}<br /><span className="text-[var(--crtr-yellow)]">{edge.title[1]}))</span>
          </h2>
        </div>
        <div className="reveal space-y-5 md:pt-6">
          {edge.body.map((b, i) => (
            <p key={i} className={i === 0 ? "font-heavy text-2xl text-white" : "text-lg leading-relaxed text-white/70"}>{b}</p>
          ))}
        </div>
      </div>
    </section>
  );
}
